// GET /public/b/:token — the postventa share-link provider portal's
// read endpoint (S2). Unauthenticated, bundle-token-addressed. See
// apps/core-api/src/db/schema.ts (shareBundles, shareBundleItems) for
// the data model and ADR-0069 for the full design.
//
// Council-settled shape:
//   - items addressed by share_bundle_items.id (never the underlying
//     share_links token) in both the response and any later write call
//   - every miss reason (bundle expired/revoked/not-found, item
//     expired/revoked/not-found) collapses into one uniform "invalid"
//     state — no existence oracle for an unauthenticated caller
//   - `recipe` lives once at the envelope root (all items in a bundle
//     share one org, by the single-asset-per-bundle constraint), not
//     duplicated per item
//   - minimized per-item view: no organizationId, assetId, reportId,
//     providerId, or audioTranscript — this is a third party outside
//     the org, narrower even than the /public/:token report reader
import { publicVisitContactSchema } from "./visit-contact.js";
import { DERIVED_SEVERITIES, OBSERVATION_PHASES, OBSERVATION_VISIT_CONFIRMATIONS, OBSERVATION_VISIT_PREFERRED_DAYS_MAX, OBSERVATION_VISIT_SLOTS, evidenceCreateSchema, evidenceSchema, observationVisitDateSchema, observationVisitTimeSchema, visitRangeValid, } from "@bitacorapro/core-engine";
import { z } from "zod";
import { publicRecipeSchema } from "./recipes.js";
// ADR-0107 — a single evidence photo served through the short-TTL read
// presign. `.pick()`, never `.omit()` (ADR-0104 §Decision 7): a new
// engine field on `evidence` doesn't leak to this unauthenticated
// surface by default, it has to be added here deliberately.
export const publicSignedMediaSchema = evidenceSchema.pick({ id: true, url: true }).extend({
    expiresAt: z.string().datetime(),
    // ADR-0107 §Decision 6 correction (2026-08-26 Postscript) — an
    // explicit discriminator instead of relying on a far-future
    // `expiresAt` as an implicit signal. `false` means this row's
    // `evidence.objectKey` was NULL at read time, so `url` is the
    // pre-presign stable object URL and is NOT actually revocable by
    // TTL — `true` means a real short-TTL presign. Machine-checkable
    // instead of convention-checkable.
    revocable: z.boolean(),
});
// Motivos cerrados de «solicitar nueva visita» (ADR-0123). Declarados
// antes del ítem del bundle porque `visitRequest` los usa. Etiquetas
// visibles: PUBLIC_VISIT_REASON_LABEL en observation-labels.ts.
export const PUBLIC_VISIT_REASONS = [
    "sin_acceso",
    "falta_material",
    "requiere_otro_especialista",
    "trabajo_mayor",
];
// Nota libre de una superficie sin sesión (/attend, /request-visit). Se
// limpia ANTES de validar: fuera los controles C0 salvo \t \n \r y los
// controles bidi (U+202A–U+202E, U+2066–U+2069) que permiten disfrazar el
// texto en el dashboard o en un correo; luego trim. Una nota que queda
// vacía se trata como ausente, no como error.
// eslint-disable-next-line no-control-regex
const PUBLIC_NOTE_STRIP = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u202A-\u202E\u2066-\u2069]/g;
export function cleanPublicNote(raw) {
    return raw.replace(PUBLIC_NOTE_STRIP, "").trim();
}
export function publicNote(max) {
    return z.preprocess((v) => (typeof v === "string" ? cleanPublicNote(v) || undefined : v), z.string().min(1).max(max).optional());
}
// Pedido de visita tal como lo ve el contratista que lo hizo: motivo
// (clave del enum; el cliente la traduce), detalle y fecha.
export const publicBundleVisitRequestSchema = z
    .object({
    reason: z.enum(PUBLIC_VISIT_REASONS),
    note: z.string().max(500).nullable(),
    requestedAt: z.string().datetime(),
})
    .strict();
// Codec del pedido de visita sobre `finding_events.note` (sin migración):
// `motivo` o `motivo · detalle`. Se parte en el PRIMER « · » — el enum no
// contiene ese separador, así que un detalle que sí lo contenga vuelve
// intacto. Un motivo fuera del enum decodifica a null (dato legado o
// corrupto: el cliente no pinta el aviso).
const VISIT_NOTE_SEPARATOR = " · ";
export function encodeVisitRequestNote(v) {
    return v.note ? `${v.reason}${VISIT_NOTE_SEPARATOR}${v.note}` : v.reason;
}
export function decodeVisitRequestNote(raw) {
    if (raw == null)
        return null;
    const at = raw.indexOf(VISIT_NOTE_SEPARATOR);
    const reason = at === -1 ? raw : raw.slice(0, at);
    const note = at === -1 ? null : raw.slice(at + VISIT_NOTE_SEPARATOR.length);
    if (!PUBLIC_VISIT_REASONS.includes(reason))
        return null;
    return { reason: reason, note: note === "" ? null : note };
}
export const publicBundleParams = z.object({
    token: z
        .string()
        .min(32)
        .max(128)
        .regex(/^[A-Za-z0-9_-]+$/, { message: "invalid public token shape" }),
});
// ADR-0073 — a provider's own prior proof submission through THIS item's
// share link. Shared verbatim between the read response (below) and the
// attend-with-proof write response, so the two stay in lockstep by
// construction rather than by discipline. One note per submission, not
// per photo — the provider is describing the observation, not
// captioning individual photos.
export const publicBundleProofSchema = z.object({
    photos: z.array(z.object({ url: z.string().url(), submittedAt: z.string().datetime() })),
    note: z.string().nullable(),
    submittedAt: z.string().datetime().nullable(),
});
// ADR-0160 — la visita vigente tal como la ve el contratista asignado.
export const publicBundleItemVisitSchema = z
    .object({
    date: observationVisitDateSchema,
    startTime: observationVisitTimeSchema,
    endTime: observationVisitTimeSchema,
    confirmation: z.enum(OBSERVATION_VISIT_CONFIRMATIONS).nullable(),
    /** ADR-0161 — EXCEPCIÓN S2 declarada (public-minimization.test.ts):
     *  los días y la franja que eligió el propietario al pedir otro
     *  horario. Es un dato ESTRUCTURADO, sin texto libre, y el contratista
     *  lo necesita para proponer otra visita que sirva. Sólo con
     *  `other_time_requested`, sólo para la respuesta a la última
     *  propuesta y sólo mientras el enlace puede actuar. */
    ownerPreference: z
        .object({
        dates: z.array(observationVisitDateSchema).min(1).max(OBSERVATION_VISIT_PREFERRED_DAYS_MAX),
        slot: z.enum(OBSERVATION_VISIT_SLOTS),
    })
        .strict()
        .nullable()
        .optional(),
})
    .strict();
// Exportado (S2) para que public-minimization.test.ts recorra la forma
// del ítem; `.strict()` porque el portal del contratista es la superficie
// más angosta de todas y no admite claves fuera del contrato.
export const publicBundleObservationSchema = z
    .object({
    itemId: z.string().uuid(),
    folio: z.string().nullable(),
    areaName: z.string(),
    element: z.string().nullable(),
    category: z.string(),
    severity: z.string(),
    humanDescription: z.string().nullable(),
    aiDescription: z.string().nullable(),
    aiRecommendation: z.string().nullable(),
    /** Titular corto de la IA (ADR-0117) — el mismo título que ve el
     *  propietario en su tarjeta. Describe la falla, no a una persona.
     *  Opcional (ADR-0021 D6): un core-api anterior no lo manda y el
     *  cliente cae a `aiDescription`. */
    aiTitle: z.string().nullable().optional(),
    /** Lo que el propietario dictó al levantar la observación, como
     *  TRANSCRIPCIÓN. Nunca un reproductor: la regla dura 8 prohíbe audio
     *  en superficies de operador, y el contratista es una de ellas. Sin
     *  esto, el asignado llega a la puerta sabiendo la categoría y el
     *  recinto pero no lo que la persona describió. */
    ownerTranscript: z.string().nullable(),
    // Org-authored evidence only (ADR-0073) — never a share-link upload
    // from ANY bundle, including this item's own. A provider's own prior
    // submissions live in `submittedProof` below, scoped to this item's
    // link specifically.
    //
    // ADR-0107 (2026-08-26 decision: expand-then-contract, not a single
    // breaking rename) — `evidence` is the new, presigned shape; carries
    // the same photos as `evidenceUrls` but with a short-TTL signed `url`
    // where `objectKey` exists, and `expiresAt`/`revocable` per item.
    // `evidenceUrls` stays wire-identical to before it for one PWA
    // release cycle so a stale cached build doesn't break outright; a
    // later pass drops it once client telemetry shows no build still
    // reading it. Do not add new consumers of `evidenceUrls` — read
    // `evidence` instead.
    evidenceUrls: z.array(z.string().url()),
    evidence: z.array(publicSignedMediaSchema),
    submittedProof: publicBundleProofSchema,
    observationStatus: z.string().nullable(),
    observationStatusAt: z.string().datetime().nullable(),
    // ── Pills del portal (handoff del detalle de observación). Todos
    // opcionales (ADR-0021 D6): un core-api anterior no los manda y el
    // cliente cae a `severity` / sin modificador / `areaName`.
    /** La S-code que el pill de criticidad pinta (derivada o
     *  `legacyDisplayAs` de la receta). Nunca se persiste. */
    effectiveSeverity: z.enum(DERIVED_SEVERITIES).nullable().optional(),
    /** La fase que deriva `observationPhase()` del engine. El portal la
     *  necesita del servidor porque su receta pública
     *  (`publicRecipeSchema`) no trae el grafo de transiciones —aristas
     *  ni actores no cruzan un borde sin sesión— y sin grafo el cliente
     *  no puede derivarla. Es la que decide el pill de estado
     *  («Asignada», «Lista para revisar», «Cerrada»…). */
    observationPhase: z.enum(OBSERVATION_PHASES).optional(),
    /** Reaperturas (ciclo − 1), derivado por `observationPhase()` del
     *  engine. El portal no recibe `observationCycle`: sólo el conteo. */
    reopenCount: z.number().int().nonnegative().optional(),
    /** Overlay de cierre (ADR-0105 Decisión 4), ISO o null. Sólo la
     *  fecha: el motivo del cierre no es asunto del contratista. */
    observationClosedAt: z.string().datetime().nullable().optional(),
    /** El NOMBRE del recinto, SIN código de taxonomía (regla dura 9: un
     *  código no llega a una superficie sin sesión). El cliente pinta
     *  `recintoNombre ?? areaName`. */
    recintoNombre: z.string().nullable().optional(),
    // Per-item capability, mirrored from the underlying share_links row
    // — the portal must not render (or attempt) an action the link
    // wasn't minted to allow.
    allowedActions: z.object({
        view: z.literal(true),
        updateStatus: z.boolean(),
        uploadEvidence: z.boolean(),
        /** ADR-0123 — «solicitar nueva visita». Es también el DISCRIMINADOR
         *  v1/v2 del portal: un enlace emitido antes de esta capacidad no la
         *  trae, no gana nada, y el cliente no le pinta el botón. Ése es el
         *  mecanismo de compatibilidad completo — no hay migración de
         *  enlaces vivos. */
        requestVisit: z.boolean().optional(),
        /** ADR-0160 — «agendar / cambiar la visita». Sólo los enlaces
         *  emitidos desde esta capacidad (`schedule_visit`) y sólo con
         *  contraparte externa (postventa). Ausente o false: sin botón. */
        scheduleVisit: z.boolean().optional(),
    }),
    /** True cuando el asignado ya pidió una visita nueva sobre este ítem.
     *  Se deriva del último `finding_events` de ese tipo; no hay columna.
     *  Sirve para no repetir el pedido, no para cambiar el estado — pedir
     *  una visita NO mueve la observación. */
    visitRequested: z.boolean().optional(),
    /** El último pedido de visita de ESTE enlace mientras la observación
     *  sigue asignada a él (null si no hay o si ya no es accionable). Es
     *  lo que pinta el aviso «Nueva visita solicitada · {motivo}». Nunca
     *  el pedido de otro enlace. `visitRequested` = `visitRequest !== null`. */
    visitRequest: publicBundleVisitRequestSchema.nullable().optional(),
    /** ADR-0160 — la visita vigente mientras la observación sigue con
     *  ESTE enlace (null si no hay o si ya no es accionable). Sin el
     *  nombre de la empresa: es él. `confirmation` = `pending` si la
     *  propuso él y el propietario aún no la confirma. */
    visit: publicBundleItemVisitSchema.nullable().optional(),
    /** ADR-0160 — el DÍA del plazo de la observación (Santiago), nunca la
     *  hora. Ya lo recibe en el correo de asignación. */
    dueDate: observationVisitDateSchema.nullable().optional(),
    /** ADR-0160 — cuántas escrituras de visita le quedan a este enlace
     *  (`OBSERVATION_VISIT_CONTRACTOR_MAX_CHANGES` menos las hechas). Sólo
     *  con `allowedActions.scheduleVisit`. */
    visitChangesLeft: z.number().int().min(0).optional(),
    /** Cuándo se levantó la observación (findings.created_at), para la
     *  fecha de la tarjeta. */
    reportedAt: z.string().datetime().nullable().optional(),
    /** ADR-0161 — clave OPACA de la unidad dentro de este bundle (un hash
     *  recortado; no expone el id del asset): agrupa en el cliente las
     *  observaciones de la misma unidad para proponer una sola visita. */
    unitKey: z.string().min(1).max(32).optional(),
})
    .strict();
export const publicBundleItemSchema = z.discriminatedUnion("state", [
    publicBundleObservationSchema.extend({ state: z.literal("ok") }),
    z.object({ itemId: z.string().uuid(), state: z.literal("invalid") }),
]);
export const publicBundleResponse = z
    .object({
    items: z.array(publicBundleItemSchema),
    recipientLabel: z.string().nullable(),
    recipe: publicRecipeSchema,
    /** La puerta y a quién llamar. Unidad, proyecto y dirección: lo justo
     *  para verificar que está en el lugar correcto.
     *
     *  ADR-0130 (enmienda 0123 §3): también el propietario de ESA unidad —
     *  nombre, teléfono y correo, para que el contratista coordine la
     *  visita con "Llamar" / "Escribir". Nada más: nunca el RUT, nunca
     *  otra unidad. Los tres son null cuando el dato falta, siempre en un
     *  enlace sin la capacidad `requestVisit` (el contrato v1: enlaces
     *  postventa anteriores a ADR-0123), y siempre cuando el asset_party
     *  de la receta no es una contraparte externa (facility, ADR-0134: el
     *  jefe de tienda es personal y los `owner_*` de una tienda no se
     *  publican). Un enlace
     *  revocado o vencido no llega acá: responde 404 sin cuerpo de datos.
     *  El endurecimiento Ley 21.719 de esta exposición está diferido a
     *  #0705 por decisión del founder. */
    asset: z
        .object({
        addressLine: z.string().nullable(),
        unitLabel: z.string().nullable(),
        assetGroupName: z.string().nullable(),
        ownerName: z.string().nullable(),
        ownerPhone: z.string().nullable(),
        ownerEmail: z.string().email().nullable(),
        /** ADR-0150 (founder, 2026-10-16) — «Coordina el acceso con»: el
         *  contacto del arrendatario, sólo nombre y teléfono. Null salvo
         *  que la unidad tenga contacto vigente Y alguna observación de
         *  este enlace esté hoy con el contratista (asignada, no lista).
         *  Opcional (ADR-0021 D6): un core-api anterior no lo manda. La
         *  respuesta va con `Cache-Control: no-store`. */
        visitContact: publicVisitContactSchema.nullable().optional(),
    })
        .nullable(),
    /** La organización que asignó (`organizations.name`), para la línea
     *  de coordinación del portal cuando no hay contacto que mostrar
     *  (facility: su asset_party es personal, no un propietario — ver
     *  `asset` arriba). Opcional (ADR-0021 D6): un core-api anterior no
     *  lo manda y el portal cae a una frase sin nombre. */
    organizationName: z.string().nullable().optional(),
    /** Quién recibe y revisa el trabajo del proveedor (ADR-0134): el
     *  propietario (`external`, postventa) o el personal de la tienda
     *  (`staff`, facility). Null sin carril asset_party. Decide las
     *  palabras del portal («el propietario» / «el equipo de la tienda»)
     *  sin ramificar por slug. Opcional (ADR-0021 D6): ausente se lee
     *  como `external`, el copy de siempre. */
    assetPartyAudience: z.enum(["external", "staff"]).nullable().optional(),
})
    .strict();
export const publicBundleItemActionParams = z.object({
    token: publicBundleParams.shape.token,
    itemId: z.string().uuid(),
});
// POST /public/b/:token/items/:itemId/proof/upload-url — presign step
// (ADR-0073). Issues a short-TTL, server-scoped presigned PUT URL; the
// caller never names an org, a bucket, or a key prefix — those are all
// resolved server-side from the token→bundle→item chain.
export const publicBundleProofUploadUrlBody = z
    .object({
    contentType: z.enum(["image/jpeg", "image/png", "image/webp"]),
    contentLength: z
        .number()
        .int()
        .min(1024)
        .max(10 * 1024 * 1024),
})
    .strict();
export const publicBundleProofUploadUrlResponse = z.object({
    uploadUrl: z.string().url(),
    objectKey: z.string(),
    publicUrl: z.string().url(),
    expiresAt: z.string().datetime(),
    // The client MUST replay these headers verbatim on the PUT (mirrors
    // media-svc's Presigner.presignUpload — signableHeaders is pinned to
    // content-type, so an omitted or altered header 403s the upload).
    requiredHeaders: z.record(z.string(), z.string()),
});
// A single evidence object key, derived from the engine's own
// (optional, in that context) objectKey field rather than hand-rolled —
// required and non-empty here, since every photo on this write path
// must have gone through the presign step first.
const publicBundleProofObjectKey = evidenceCreateSchema.shape.objectKey.unwrap().min(1);
// The body for POST /public/b/:token/items/:itemId/attend (ADR-0073).
// `.strict()` and — this is the load-bearing property, not the
// bodylessness the original design leaned on — its member set has ZERO
// status-shaped fields. The server always resolves the one
// recipe-declared status with `settableBy: ["share_link", ...]` and sets
// that, unconditionally; nothing the client sends can influence which
// status gets written. This is L1 of the three-layer enforcement
// (resolveObservationStatus's settableBy check is L2; the
// finding_events CHECK constraint is L3). `objectKeys` never carries a
// client-supplied URL — an unauthenticated caller may not name a host;
// the server derives each `url` from `objectKey` + the configured
// bucket base after regex-validating the key against the prefix it
// itself minted at presign time. Proof is required — at least one
// photo or a non-empty note — per the resolved product decision that a
// bare status flip with no evidence is no longer a valid submit.
export const publicBundleItemAttendBody = z
    .object({
    objectKeys: z.array(publicBundleProofObjectKey).max(6).default([]),
    note: publicNote(2000),
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.objectKeys.length === 0 && !v.note) {
        ctx.addIssue({
            code: "custom",
            message: "At least one photo or a note is required",
            params: { code: "PROOF_REQUIRED" },
        });
    }
});
export const publicBundleItemAttendResponse = z.object({
    itemId: z.string().uuid(),
    observationStatus: z.string(),
    observationStatusLabel: z.string(),
    observationStatusAt: z.string().datetime(),
    proof: publicBundleProofSchema,
});
// POST /public/b/:token/items/:itemId/request-visit — el asignado no
// pudo ejecutar y pide volver (motivos: ver PUBLIC_VISIT_REASONS arriba).
//
// NO cambia el estado: la observación sigue asignada a él y lo que
// cambia es que la inmobiliaria recibe la señal para destrabar. Es la
// salida del riesgo que abrió la decisión 15 — si sólo el asignado mueve
// `asignada → resuelta`, un asignado que no puede ejecutar necesita una
// forma de decirlo que no sea el silencio.
export const publicBundleRequestVisitBody = z
    .object({
    // Set cerrado a propósito: el motivo se usa para destrabar, y un
    // texto libre obligatorio produce «no se pudo» cuatro veces de
    // cuatro formas distintas.
    reason: z.enum(PUBLIC_VISIT_REASONS),
    note: publicNote(500),
    /** Fotos de lo que impidió ejecutar (handoff 4.2). Opcionales: el
     *  aviso no las exige. Mismo carril que la prueba de /attend —
     *  presign por /proof/upload-url, clave atada al prefijo del enlace,
     *  HEAD-verify en el servidor — y mismo tope de 6. */
    objectKeys: z.array(publicBundleProofObjectKey).max(6).default([]),
})
    .strict();
export const publicBundleRequestVisitResponse = z.object({
    itemId: z.string().uuid(),
    visitRequested: z.literal(true),
    /** Se devuelve el estado para que el cliente vea, explícitamente, que
     *  NO cambió. */
    observationStatus: z.string().nullable(),
});
// POST /public/b/:token/items/:itemId/visit — ADR-0160: el contratista
// agenda o cambia la visita desde su enlace. La misma forma que la del
// jefe (`findingVisitScheduleSchema` de findings.ts, que no se importa
// acá para no cerrar un ciclo findings ↔ public-bundle): día de Santiago
// y rango de horas en pasos de 30 min, el término después del inicio. El
// servidor valida además el horizonte del contratista y la hora pasada.
export const publicBundleScheduleVisitBody = z
    .object({
    date: observationVisitDateSchema,
    startTime: observationVisitTimeSchema,
    endTime: observationVisitTimeSchema,
    /** ADR-0161 — la misma visita para otras observaciones de la MISMA
     *  unidad en este bundle (todo o nada): el propietario recibe un solo
     *  aviso y responde una sola vez. */
    alsoItemIds: z
        .array(z.string().uuid())
        .max(19)
        .refine((ids) => new Set(ids).size === ids.length, { message: "alsoItemIds must be unique" })
        .optional(),
})
    .strict()
    .refine((v) => visitRangeValid(v.startTime, v.endTime), {
    message: "endTime must be after startTime",
    path: ["endTime"],
});
export const publicBundleScheduleVisitResponse = z
    .object({
    itemId: z.string().uuid(),
    /** false = el mismo rango que ya estaba: nada se escribió ni se
     *  descontó del tope. */
    changed: z.boolean(),
    rescheduled: z.boolean(),
    visit: z.object({
        date: observationVisitDateSchema,
        startTime: observationVisitTimeSchema,
        endTime: observationVisitTimeSchema,
    }),
    confirmation: z.enum(OBSERVATION_VISIT_CONFIRMATIONS).nullable(),
    /** El día del plazo (Santiago) o null. */
    dueDate: observationVisitDateSchema.nullable(),
    /** La visita cae después del plazo: advertencia, nunca bloqueo. */
    afterDue: z.boolean(),
    visitChangesLeft: z.number().int().min(0),
    /** ADR-0161 — el resultado de cada `alsoItemIds`. */
    also: z
        .array(z
        .object({
        itemId: z.string().uuid(),
        changed: z.boolean(),
        visitChangesLeft: z.number().int().min(0),
    })
        .strict())
        .optional(),
})
    .strict();
//# sourceMappingURL=public-bundle.js.map